import { useEffect, useState } from 'react'
import { ClockIcon } from '@heroicons/react/24/outline'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@duck/ui'
import { useTar, TranslateFunction } from '@/modules/i18n'

interface RelativeDateTimeProps {
  isoDateString: string | null | undefined
  fallbackText?: string
  className?: string
  makeLabel?: (time: string) => string
  /** When true, show only the duration (e.g. "5 minutes") without "In" or "ago". */
  onlyDuration?: boolean
  absoluteDate?: boolean
}


const formatTooltipDate = (isoString: string, t: TranslateFunction): string => {
  const ts = Date.parse(isoString)
  if (Number.isNaN(ts)) {
    return t('times.invalidDate')
  }

  // Date object automatically displays in local timezone
  const date = new Date(ts)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day}/${month} ${hours}:${minutes}`
}

const formatAbsoluteDate = (isoString: string, t: TranslateFunction, locale: string): string => {
  const ts = Date.parse(isoString)
  if (Number.isNaN(ts)) {
    return t('times.invalidDate')
  }

  const date = new Date(ts)
  const dayName = date.toLocaleDateString(locale, { weekday: 'short' })
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${dayName}, ${day}/${month} - ${hours}:${minutes}`
}

const getDiffText = (diffInMs: number, t: TranslateFunction): string | null => {
  const diffInSeconds = Math.floor(diffInMs / 1000)
  const diffInMinutes = Math.floor(diffInSeconds / 60)
  const diffInHours = Math.floor(diffInMinutes / 60)
  const diffInDays = Math.floor(diffInHours / 24)

  // If less than 1 minute away, show seconds
  if (diffInMinutes < 1) {
    return t('times.xSeconds', { x: diffInSeconds })
  }

  // If less than 1 hour away, show minutes
  if (diffInHours < 1) {
    return diffInMinutes === 1 ? t('times.oneMinute') : t('times.xMinutes', { x: diffInMinutes })
  }

  // If less than 10 days away, show relative time
  if (diffInDays < 10) {
    if (diffInHours <= 30) {
      return diffInHours === 1 ? t('times.oneHour') : t('times.xHours', { x: diffInHours })
    } else {
      return diffInDays === 1 ? t('times.oneDay') : t('times.xDays', { x: diffInDays })
    }
  }

  return null // No relative time
}

const formatDateTime = (isoString: string, t: TranslateFunction, locale: string, onlyDuration: boolean): string => {
  const ts = Date.parse(isoString)
  if (Number.isNaN(ts)) {
    return t('times.invalidDate')
  }

  const date = new Date(ts)
  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const isFuture = diffInMs < 0
  const absDiffInMs = Math.abs(diffInMs)

  if (absDiffInMs < 10_000) {
    return isFuture ? t('times.verySoon') : t('times.justNow')
  }

  const diffText = getDiffText(absDiffInMs, t)
  if (diffText) {
    if (onlyDuration) {
      return diffText
    }
    return isFuture ? t('times.inX', { x: diffText }) : t('times.xAgo', { x: diffText })
  }

  // Otherwise show short date format
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

export const RelativeDateTime = ({ isoDateString, fallbackText, className, makeLabel, absoluteDate, onlyDuration = false }: RelativeDateTimeProps) => {
  const [updateTrigger, setUpdateTrigger] = useState(0)
  const { t, locale } = useTar()
  useEffect(() => {
    let timeout = 60_000 * 60 // Rerender every 1 hour
    if (isoDateString) {
      const timeDiff = Math.abs(new Date(isoDateString).getTime() - Date.now())
      if (timeDiff < 60_000) { // less than 1 minute
        timeout = 5_000 // Rerender every 5 seconds
      } else if (timeDiff < 60_000 * 60) { // less than 1 hour
        timeout = 1_000 * 60 // Rerender every 1 minute
      }
    }
    const timeoutToken = setTimeout(() => {
      setUpdateTrigger(prev => prev + 1)
    }, timeout)

    return () => clearTimeout(timeoutToken)
  }, [isoDateString, updateTrigger])



  const relativeText = isoDateString ? formatDateTime(isoDateString, t, locale, onlyDuration) : fallbackText
  const absoluteText = isoDateString ? formatAbsoluteDate(isoDateString, t, locale) : fallbackText
  const displayText = absoluteDate ? absoluteText : relativeText
  const tooltipContent = absoluteDate
    ? (isoDateString ? relativeText : '')
    : (isoDateString ? formatTooltipDate(isoDateString, t) : '')

  if (!isoDateString || !tooltipContent) {
    return (
      <span className={className}>
        {makeLabel ? makeLabel(displayText || '') : displayText}
      </span>
    )
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={className}>
            {makeLabel ? makeLabel(displayText || '') : displayText}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="flex items-center gap-1.5">
            <ClockIcon className="w-4 h-4" />
            {tooltipContent}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
} 